// Outstanding users page: training debt action list. Uses
// buildOutstandingStats from CoursesPage so the page-level KPI and this
// table stay aligned.
//
// Calculations: training-debt formula = courses where sent_at > 14d ago
// AND completed_at IS NULL. Lag column shows days since oldest overdue
// send.
//
// Postgres mapping: view `outstanding_users_v` (email, name, dept,
// overdue_count, oldest_sent_at) refreshed nightly.
// Integration hook: `useOutstandingUsersQuery({ dept, scope })` cache 1h.
import React, { useMemo } from 'react';
import { Box, Button } from '@mui/material';
import { motion } from 'motion/react';
import { COURSE_SEND_LOG } from '../data';
import useDashboardData, { logInPeriod } from '../hooks/useDashboardData';
import { buildOutstandingStats, OUTSTANDING_USERS_LIMIT } from './CoursesPage';
import { toServerScope, userScopeIsEmails } from '../serverScope';
import DataTable from './DataTable';
import WidgetState from './WidgetState';

const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.07 } } };

function exportCSV(rows) {
    const headers = ['Name', 'Email', 'Department', 'Location', 'Outstanding', 'Sent', 'Days Overdue'];
    const lines = [headers.join(','), ...rows.map((r) => [
        `"${r.name}"`, `"${r.email || ''}"`, `"${r.dept}"`, `"${r.location || ''}"`,
        r.outstanding, r.sent, r.oldestDays,
    ].join(','))];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'outstanding-users.csv'; a.click();
    URL.revokeObjectURL(url);
}

function applyScope(log, scope) {
    const s = scope || {};
    const isIn = (val, vs) => { if (!vs) return true; const a = Array.isArray(val) ? val : [val]; return Array.isArray(vs) ? a.some((x) => vs.includes(x)) : a.includes(vs); };
    return log.filter((e) => {
        if (!isIn(e.dept,       s.department)) return false;
        if (!isIn(e.location,   s.location))   return false;
        if (!isIn(e.city,       s.city))       return false;
        if (!isIn(e.country,    s.country))    return false;
        if (!isIn(e.company,    s.company))    return false;
        if (!isIn(e.state,      s.state))      return false;
        if (!isIn(e.courseName, s.course))     return false;
        if (s.user) {
            const userVals = Array.isArray(s.user) ? s.user : [s.user];
            const full = `${e.firstName} ${e.lastName}`;
            if (!userVals.some((v) => e.email === v || full === v)) return false;
        }
        return true;
    });
}

export default function OutstandingUsersPage({ drill, scope, period, startDate, endDate }) {
    const clientOnlyScope = !userScopeIsEmails(scope);
    const odQ = useDashboardData({ metric: 'courses', aggregation: 'course_detail', limit: OUTSTANDING_USERS_LIMIT, period, startDate, endDate, scope: toServerScope(scope, { course: true }) }, { skip: clientOnlyScope });
    const odAgg = (!clientOnlyScope && Array.isArray(odQ.data?.courseDetail?.outstandingUsers)) ? odQ.data.courseDetail.outstandingUsers : null;
    const rows = useMemo(() => {
        if (odAgg) return odAgg.map((u) => ({ name: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email, email: u.email, dept: u.dept || '', location: u.location || '', outstanding: u.outstanding, oldestDays: u.oldestDays, sent: u.sent }));
        return buildOutstandingStats(applyScope(logInPeriod(COURSE_SEND_LOG, { period, startDate, endDate }), scope));
    }, [scope, odAgg, period, startDate, endDate]);

    const columns = [
        { id: 'name',        label: 'Name',        always: true,   sortable: true, type: 'string', accessor: (u) => u.name },
        { id: 'email',       label: 'Email',       defaultOn: true,sortable: true, type: 'string', accessor: (u) => u.email || '' },
        { id: 'dept',        label: 'Department',  defaultOn: true,sortable: true, type: 'string', accessor: (u) => u.dept },
        { id: 'location',    label: 'Location',    defaultOn: true,sortable: true, type: 'string', accessor: (u) => u.location || '' },
        { id: 'outstanding', label: 'Outstanding', defaultOn: true,sortable: true, type: 'number', accessor: (u) => u.outstanding, align: 'right',
          render: (u) => (
            <Box sx={{ fontWeight: 700, color: 'warning.main' }}>
                {u.outstanding}
                <Box component="span" sx={{ fontSize: 10, fontWeight: 500, color: 'text.disabled', ml: 0.5 }}>/ {u.sent}</Box>
            </Box>
          ) },
        { id: 'oldestDays',  label: 'Overdue',     defaultOn: true,sortable: true, type: 'number', accessor: (u) => u.oldestDays, align: 'right',
          render: (u) => (
            <Box sx={{ fontWeight: 600, color: u.oldestDays >= 30 ? '#FF5252' : u.oldestDays >= 14 ? '#FFCA28' : 'text.secondary' }}>
                {u.oldestDays}d
            </Box>
          ) },
        { id: 'sent',        label: 'Sent',        sortable: true, type: 'number', accessor: (u) => u.sent, align: 'right' },
    ];

    return (
        <motion.div variants={stagger} initial="hidden" animate="visible" style={{ padding: '0 24px 48px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <motion.div variants={fadeUp}>
                <WidgetState queries={[odQ]}>
                <DataTable
                    title="Users with outstanding courses"
                    subtitle='"Outstanding" = at least one assigned course past its due date and still incomplete.'
                    columns={columns}
                    rows={rows}
                    rowKey={(u) => u.email || u.name}
                    onRowClick={(u) => drill && drill('user-detail', u.name, { type: 'user', value: u.email || u.name })}
                    initialSort={{ by: 'outstanding', dir: 'desc' }}
                    storageKey="rbx-outstanding-cols"
                    emptyMessage="No outstanding users"
                    headerActions={
                        <Button
                            onClick={() => exportCSV(rows)}
                            variant="outlined" size="small"
                            sx={{ borderRadius: '4px', borderColor: 'secondary.main', color: 'secondary.main', fontSize: 11, textTransform: 'none', whiteSpace: 'nowrap', '&:hover': { bgcolor: 'secondary.main', color: 'common.white' } }}
                        >
                            Export CSV ({rows.length})
                        </Button>
                    }
                />
                </WidgetState>
            </motion.div>
        </motion.div>
    );
}
